import { defineComponent, reactive, SetupContext, readonly, ref, onMounted, computed, toRefs, watch } from '@vue/composition-api';
import CommonCategory from '@/components/common/common-content-category.component.vue';         // カテゴリ選択共通コンポーネント
import vueRouter from '@/router/index';
import RichTextEditor from '../common/rich-text-editor.component.vue';
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import { AttachmentFile, ReferenceUrl } from '@/shared/type';
import HttpStatus from 'http-status-codes';
import SystemConst from '@/shared/system-const';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                   // Windowサイズ
    title: string;                                          // タイトル
    body: string;                                           // 掲載内容
    updateDate: string;                                     // 更新日
    referenceUrls: ReferenceUrl[];                          // 参考URL
    isUrlDisplay: boolean;                                  // 参考URL表示切替
    attachmentFiles: AttachmentFile[];                      // 添付ファイル
    isAttachDisplay: boolean;                               // 添付ファイル表示切替
    isIntChangeCategory: boolean;                           // 初期表示によるカテゴリの変更か
}

/**
 * 教育用コンテンツ詳細画面コンポーネント
 */
export default defineComponent({
    components: { RichTextEditor, CommonCategory, CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const commonRef = ref<any>(null);                                       // メッセージダイアログ
        const categoryRef = ref<any>(null);                                     // カテゴリー選択
        const router = vueRouter;                                               // ルータ
        const pondRef = ref<any>(null);                                         // FilePondへの参照
        const detailHeight = SystemConst.DETAIL_HEIGHT.CONTENT;                 // テーブル高さ調整用変数
        const detailTextHeight = SystemConst.DETAIL_HEIGHT.CONTENT_BODY;        // 掲載内容表示高さ調整用変数
        const contentId = computed(() => ctx.root.$route.params.id).value;      // コンテンツID
        const fileIconList = SystemConst.FILE_EXTENSION_ICON;                   // 添付ファイル拡張子アイコンリスト

        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                         // ウィンドウサイズ
            title: '',                                                          // タイトル
            body: '',                                                           // 掲載内容
            updateDate: '',                                                     // 更新日
            referenceUrls: [],                                                  // 参考URL
            isUrlDisplay: false,                                                // 参考URL表示切替
            attachmentFiles: [],                                                // 添付ファイル
            isAttachDisplay: false,                                             // 添付ファイル表示切替
            isIntChangeCategory: false                                          // 初期表示によるカテゴリの変更か
        });

        // カテゴリ選択のアクティブノードを監視する
        const { activeNode } = toRefs(ctx.root.$store.state.content);
        // カテゴリが選択された場合
        watch(activeNode, (newVal, oldVal) => {
            // 初期表示でない、かつホーム画面からの遷移でない場合
            if (oldVal.length !== 0 && !state.isIntChangeCategory) {
                // 教育用コンテンツ一覧画面に遷移する
                selectCategory();
            }
            // 初期表示によるカテゴリの変更フラグをfalseにする
            state.isIntChangeCategory = false;
        })

        // 表示内容の設定
        onMounted(() => {
            // カテゴリデータデータの取得関数呼び出し
            getCategory();

            // 初期表示データの取得関数呼び出し
            getDetail();
        });

        /**
         * 初期表示
         */
        created();
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-file-document-outline');

            // mainTitleを「教育用コンテンツ詳細」に変更
            ctx.root.$store.commit('setMainTitle', '教育用コンテンツ詳細');
        }

        /**
         * カテゴリデータの取得
         */
        async function getCategory() {
            // カテゴリ取得
            categoryRef.value.getCategory();
        }

        /**
         * 初期表示データの取得
         */
        async function getDetail() {
            // コンテンツIDをパラメータとして詳細データを取得する
            const response = await ctx.root.$store.dispatch('content/getContentDetail',
                {
                    contentId: contentId
                }
            );

            // API通信のレスポンスが正常に取得できなかった場合
            if (response.status !== HttpStatus.OK) {
                if (response.status === HttpStatus.NOT_FOUND || response.status === HttpStatus.BAD_REQUEST) {
                    // 400, 404エラーの場合、エラーメッセージを表示して一覧画面へ戻る
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/content/list');
                    return;
                }
                // その他のエラーの場合、エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // ストアのアクティブなカテゴリと取得したカテゴリIDが一致しない場合
            if (ctx.root.$store.state.content.activeNode[0] !== String(response.data.contentDetail.categoryId)) {
                // 初期表示によるカテゴリの変更フラグをtrueにする
                state.isIntChangeCategory = true;
                // 取得したカテゴリIDをアクティブにする
                ctx.root.$store.commit('content/setActiveNode', [String(response.data.contentDetail.categoryId)]);
                categoryRef.value.onActive();
            }

            // API通信のレスポンスが正常に取得できた場合、表示するデータを設定する
            state.title = response.data.contentDetail.title;
            state.updateDate = response.data.contentDetail.updateDate;
            // リッチテキストエディタに取得した内容を登録する
            state.body = response.data.contentDetail.body;

            // 参考URLデータを取得した場合、state.referenceUrlsに設定する
            if (response.data.contentDetail.referenceUrls.length !== 0) {
                state.referenceUrls = response.data.contentDetail.referenceUrls;

                // 参考URLの表示を切り替える
                state.isUrlDisplay = true;
            }

            // 添付ファイルデータを取得した場合、以下を添付ファイルの数分繰り返す
            const attachmentFilesNumber = response.data.contentDetail.attachmentFiles.length;
            if (attachmentFilesNumber !== 0) {
                for (let i = 0; i <= attachmentFilesNumber - 1; i++) {
                    const attachFile: AttachmentFile[] = [                  // 一時保存する添付ファイル情報
                        {
                            fileIcon: '',
                            fileSequence: 0,
                            fileName: '',
                            fileSize: ''
                        }
                    ];
                    attachFile[0].fileSequence = response.data.contentDetail.attachmentFiles[i].fileSequence;
                    attachFile[0].fileName = response.data.contentDetail.attachmentFiles[i].fileName;

                    // 添付ファイル名から拡張子部分を取得する
                    const extension = attachFile[0].fileName.substring(attachFile[0].fileName.lastIndexOf('.') + 1);
                    // アイコンのリストを作成する
                    const fileIcon = Object.keys(fileIconList);
                    // 添付ファイルアイコンを拡張子から判別し、設定する
                    Object.values(fileIconList).forEach((element, index) => {
                        // 添付ファイルの拡張子を小文字に変換して比較する
                        const iconList = element.some((extensionList) => extensionList === extension.toLowerCase());
                        if (iconList === true) {
                            // 拡張子を見つけたときの添え字番号で検索し、設定する
                            attachFile[0].fileIcon = fileIcon[index];
                        }
                        if (attachFile[0].fileIcon === '' && fileIcon.length - 1 === index) {
                            // 最後まで検索し、一致する拡張子がなかった場合
                            attachFile[0].fileIcon = fileIcon[index];
                        }
                    });

                    // 添付ファイルのサイズを「B(バイト)」表記から「KB(キロバイト)」表記へ変換する
                    const fileSize = String(Math.round((response.data.contentDetail.attachmentFiles[i].fileSize / 1024) * 1000) / 1000);
                    attachFile[0].fileSize = fileSize;

                    state.attachmentFiles.push(attachFile[0]);
                }
                // 添付ファイルの表示を切り替える
                state.isAttachDisplay = true;
            }
        }

        /**
         * 添付ファイルダウンロード処理
         * @param fileSequence ファイルシーケンス
         * @param fileName ダウンロードファイル名
         */
        async function fileDownload(fileSequence: number, fileName: string) {
            // 添付ファイルを取得するAPIを呼び出す
            // パラメータ：コンテンツID
            //            ファイルシーケンス
            const response = await ctx.root.$store.dispatch('content/attachmentDownload',
                {
                    contentId: contentId,
                    fileSequence: fileSequence,
                    fileName: fileName
                }
            );

            // API通信成否判定
            if (response.status !== HttpStatus.OK) {
                // 正常でない場合エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);
            }
            return;
        }

        /**
         * 戻るボタン押下時の処理
         */
        function back() {
            router.back();
        }

        /**
         * カテゴリ選択時のイベント
         */
        function selectCategory() {
            // ストアのページ情報をクリア
            ctx.root.$store.commit('content/clearStorePage');
            // 教育用コンテンツ一覧画面へ遷移
            router.push('/content/list');
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
            // カテゴリー選択コンポーネントのサイズも変更
            categoryRef.value.resize(state.windowSize);
        }

        return {
            state: readonly(state),
            pondRef,
            commonRef,
            categoryRef,
            detailHeight,
            detailTextHeight,
            fileDownload,
            back,
            onResize
        };
    }
});
